import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    EfeResponse,
    Field,
    IdentificationServiceRequest,
    InformationLegalRepresentativeServiceRequest,
    LegalRepresentativeServiceRequest,
    OrcValidations,
    ScreenFilterValues,
    UploadDocumentServiceRequest,
    View,
} from 'commons/models';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    IMAGE_PREFIX,
    IMAGE_PREFIX_PNG,
    INE_ID,
    Messages,
    PersonTypes,
    Roles,
    SnackbarVariants,
    ViewChildId,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import RepresentativeOfficialIdForm from './official-id-form/OfficialInfoForm';
import {convertBase64ToBlob, getBase64Extension, nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService, DocumentServices, LayoutServices, ValidationsService} from 'services';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import * as Validations from 'commons/models/dtos/validations/Validations';
import {getIdentification, validateOcrResponse} from 'commons/utils/DniValidationFunctions';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {fieldCatalogGetter} from 'hooks/FlowHook';

const errorDetail = {
    data: {
        process: Messages.IDENTIFICATION_SECTION,
        message: ' ',
        errors: [''],
    },
};

function RepresentativeOfficialId() {
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, updateModuleStep} = useFlowContext();
    const {menu, kycId, expedientId, fatherKycId} = useMenuContext();
    const {selectedRepresentative, setSelectedRepresentative, tempRepresentative} =
        useRelatedPeopleContext();

    const [fields, setFields] = React.useState<Array<Field>>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [auxLegalRepresentative, setAuxLegalRepresentative] = React.useState<any>(undefined);
    const [personaldata, setPersonalData] = React.useState<LegalRepresentativeServiceRequest>();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    async function saveDni(file: string, dniName: string) {
        if (file) {
            const extension = getBase64Extension(file);
            const fileConverted = await convertBase64ToBlob(file);
            const document: UploadDocumentServiceRequest = {
                documentKey:
                    dniName === Constants.DOC_DNI_BACK_RL
                        ? Constants.DOC_DNI_BACK_RL
                        : Constants.DOC_DNI_FRONT_RL,
                expedientId: expedientId,
                file: fileConverted,
                kycId: kycId,
                filename: `${dniName}.${extension}`,
            };
            const documentResponse: EfeResponse<any> = await DocumentServices.updateDocument(
                document
            );
            return documentResponse;
        }
    }

    function goToNextView() {
        const path = nextStep(flow, menu);
        if (Constants.MODULE_STEP !== path) {
            setIsLoading(false);
            history.push(path);
        } else {
            updateModuleStep(true);
            flow.data.expandKey = path;
            setIsLoading(false);
        }
    }

    async function validateDniResponse(
        valid: OrcValidations,
        values: IdentificationServiceRequest
    ) {
        if (valid && valid.success) {
            if (values.dniFront) {
                await saveDni(values.dniFront, Constants.DOC_DNI_FRONT_RL);
            }
            if (values.dniBack) {
                await saveDni(values.dniBack, Constants.DOC_DNI_BACK_RL);
            }
            enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                variant: SnackbarVariants.SUCCESS,
            });
            goToNextView();
        } else {
            setFields([...fields]);
            errorDetail.data.errors = valid.errors;
            showAlertError(errorDetail, '');
            setIsLoading(false);
        }
    }

    async function validateDni(values: IdentificationServiceRequest): Promise<OrcValidations> {
        const validation: OrcValidations = {
            success: false,
            errors: [],
        };
        if (values && values.dniBack && values.dniFront) {
            const dniBack = values.dniBack.replace(IMAGE_PREFIX, '').replace(IMAGE_PREFIX_PNG, '');
            const dniFront = values.dniFront
                .replace(IMAGE_PREFIX, '')
                .replace(IMAGE_PREFIX_PNG, '');
            const request: Validations.ValidationsINERequest = {
                idReverso: dniBack,
                id: dniFront,
            };
            try {
                if (values.identificationType === INE_ID) {
                    const validationResponse: EfeResponse<any> =
                        await ValidationsService.validateDni(request);
                    validation.response = validationResponse?.data;
                    if (personaldata && validationResponse && validationResponse.data) {
                        const ocrValidationsResponse = validateOcrResponse(
                            validationResponse.data,
                            personaldata
                        );
                        return ocrValidationsResponse;
                    }
                }
                validation.success = true;
                return validation;
            } catch (error: any) {
                if (error.code === 400) {
                    validation.errors = ['No se identific\u00F3 el documento.'];
                }
                return validation;
            }
        } else {
            return validation;
        }
    }

    async function onSubmit(values: IdentificationServiceRequest) {
        if (tempRepresentative?.foreignCitizen) {
            goToNextView();
            return;
        }
        try {
            const request = {...values};
            request.kycId = kycId;
            request.dniBack = '';
            request.dniFront = '';
            setIsLoading(true);
            setFields([...fields]);
            const response: EfeResponse<IdentificationServiceRequest> =
                await ClientService.saveIdentificationData(request);
            if (response && response.data) {
                setSelectedRepresentative(response.data.kycId);
                if (!tempRepresentative?.foreignCitizen && values.identificationType === '1') {
                    await validateDniResponse(await validateDni(values), values);
                } else {
                    await saveDni(values.dniFront, Constants.DOC_DNI_FRONT_RL);
                    enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                    setIsLoading(false);
                    goToNextView();
                }
            } else {
                setIsLoading(false);
                setFields([...fields]);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    async function overrideFields(personTypeKey: string, view: View | undefined) {
        if (personTypeKey && view && view.fields) {
            setFields(view.fields);
        } else {
            setFields(viewFields);
        }
    }

    async function getFieldVariant() {
        if (
            flow.data.id === ViewChildId.REAL_OWNER_OFFICIAL_ID &&
            tempRepresentative?.foreignCitizen
        ) {
            try {
                setIsLoading(true);
                const request: ScreenFilterValues = {
                    viewId: flow.data.viewId,
                    personType: PersonTypes.FISICA,
                    foreign: tempRepresentative?.foreignCitizen || false,
                    address: false,
                    rol: Roles.LEGAL_REPRESENTATIVE,
                    viewKey: ViewChildId.REAL_OWNER_OFFICIAL_ID,
                };
                const response: EfeResponse<View> = await LayoutServices.getViewByFilters(request);
                if (response.data && response.data.fields) {
                    setIsLoading(false);
                    overrideFields(PersonTypes.FISICA, response.data);
                } else {
                    enqueueSnackbar(Messages.LAYOUT_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                    overrideFields(PersonTypes.FISICA, undefined);
                    setIsLoading(false);
                }
            } catch (error) {
                setIsLoading(false);
                overrideFields(PersonTypes.FISICA, undefined);
                showAlertError(error, Messages.LAYOUT_ERROR);
            }
        } else {
            setFields(viewFields);
        }
    }

    async function getData() {
        try {
            setIsLoading(true);
            const legalRepresentativeData: EfeResponse<LegalRepresentativeServiceRequest> =
                await ClientService.getLegalRepresentativeData(kycId, expedientId);
            if (
                !legalRepresentativeData ||
                (!legalRepresentativeData.data?.foreignCitizen &&
                    !legalRepresentativeData.data?.curp)
            ) {
                history.push(HistoryRoutes.REPRESENTATIVE_PERSONAL);
                enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
            const response: EfeResponse<InformationLegalRepresentativeServiceRequest> =
                await ClientService.getLegalRepresentativeIdentification(kycId);
            setPersonalData(legalRepresentativeData.data);
            if (response && response.data) {
                response.data.dniFront = await getIdentification(
                    expedientId,
                    kycId,
                    Constants.DOC_DNI_FRONT_RL
                );
                response.data.dniBack = await getIdentification(
                    expedientId,
                    kycId,
                    Constants.DOC_DNI_BACK_RL
                );
                setAuxLegalRepresentative(response.data);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setAuxLegalRepresentative(undefined);
        }
    }

    React.useEffect(() => {
        getFieldVariant();
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            selectedRepresentative > 0 &&
            ViewKeys.LEGAL_IDENTIFICATION === flow.data.expandKey
        ) {
            getData();
        } else if (ViewKeys.LEGAL_IDENTIFICATION === flow.data.expandKey && kycId > 0) {
            history.push(HistoryRoutes.REPRESENTATIVE_PERSONAL);
            enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Identificaci\u00F3n oficial del Representante Legal o Apoderado Legal',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RepresentativeOfficialIdForm
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                            foreign={tempRepresentative?.foreignCitizen || false}
                            submitHandler={onSubmit}
                            auxLegalRepresentative={auxLegalRepresentative}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.LEGAL_IDENTIFICATION === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.REPRESENTATIVE_OFFICIAL_ID)}
                values={accordion}
            />
        </MainContent>
    );
}

export default RepresentativeOfficialId;
